import React from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import './AppShell.css';
import SidePanel from '../components/SidePanel/SidePanel';
import ConversationArea from '../components/ConversationArea/ConversationArea';
import DashboardPage from '../components/Dashboard/DashboardPage';
import InputBar from '../components/InputBar/InputBar';
import { ConversationProvider, useConversation } from '../state/ConversationContext';
import { useAuth } from '../state/AuthContext';
import IntakeWizard from '../components/Financial/IntakeWizard';
import ProfileModal from '../components/Financial/ProfileModal';
import { financialService } from '../services/financialService';
import BillingSetup from '../components/Billing/BillingSetup';
import UpgradePage from '../components/Billing/UpgradePage';
import PaymentFailedNotice from '../components/Billing/PaymentFailedNotice';
import FeedbackButton from '../components/Feedback/FeedbackButton';
import { APP_ROUTES, getChatThreadPath, isAppPath } from '../routes/routingContract';

const SETTINGS_ROUTE_TO_TAB = {
  [APP_ROUTES.profile]: 'account',
  [APP_ROUTES.financialInfo]: 'intake',
  [APP_ROUTES.accounts]: 'plaid',
  [APP_ROUTES.billing]: 'billing',
};

const SETTINGS_TAB_TO_ROUTE = {
  account: APP_ROUTES.profile,
  intake: APP_ROUTES.financialInfo,
  plaid: APP_ROUTES.accounts,
  billing: APP_ROUTES.billing,
};

const APP_MODAL_ROUTES = {
  ...SETTINGS_ROUTE_TO_TAB,
  [APP_ROUTES.feedback]: 'feedback',
};

function AppShellInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [intakeOpen, setIntakeOpen] = React.useState(false);
  const [needsPlaidConnect, setNeedsPlaidConnect] = React.useState(false);
  const { session, billingStatus, refreshBilling } = useAuth();
  const {
    loadThreads,
    loadThread,
    newConversation,
    threads,
    currentThreadId,
    upgradePrompt,
    clearUpgradePrompt,
  } = useConversation();
  const [billingSetupOpen, setBillingSetupOpen] = React.useState(false);
  const activeThread = threads.find((t) => t.id === currentThreadId) ?? null;
  const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
  const upgradePageOpen = normalizedPathname === APP_ROUTES.upgrade;
  const settingsInitialTab = SETTINGS_ROUTE_TO_TAB[normalizedPathname] || 'account';
  const settingsRouteOpen = Boolean(SETTINGS_ROUTE_TO_TAB[normalizedPathname]);
  const feedbackRouteOpen = normalizedPathname === APP_ROUTES.feedback;
  const dashboardRouteOpen = normalizedPathname === APP_ROUTES.dashboard;
  const threadRouteMatch = matchPath(APP_ROUTES.chatThread, normalizedPathname);
  const threadId = threadRouteMatch?.params?.threadId
    ? decodeURIComponent(threadRouteMatch.params.threadId)
    : null;
  const previousRouteRef = React.useRef(null);
  const upgradePageMessage = location.state?.upgradeMessage || upgradePrompt?.message || 'You can upgrade now or continue with your free trial.';

  React.useEffect(() => {
    const routeKey = threadId ? `thread:${threadId}` : normalizedPathname;
    if (previousRouteRef.current === routeKey) return;
    previousRouteRef.current = routeKey;

    if (threadId) {
      if (location.state?.preserveConversation) {
        return;
      }
      if (currentThreadId !== threadId) {
        loadThread(threadId);
      }
      return;
    }

    if (
      (normalizedPathname === APP_ROUTES.root || normalizedPathname === APP_ROUTES.chat)
    ) {
      newConversation();
    }
    // Route params are the source of truth here; context methods are intentionally not dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, normalizedPathname]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && historyOpen) setHistoryOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [historyOpen]);

  const closeRouteModal = React.useCallback(() => {
    if (APP_MODAL_ROUTES[normalizedPathname]) {
      const backgroundPath = location.state?.backgroundPath;
      const returnPath = isAppPath(backgroundPath) && !APP_MODAL_ROUTES[backgroundPath]
        ? backgroundPath
        : APP_ROUTES.chat;

      navigate(returnPath, { replace: true });
    }
  }, [location.state, navigate, normalizedPathname]);

  const handleFeedbackOpenChange = React.useCallback((nextOpen) => {
    if (nextOpen) {
      navigate(APP_ROUTES.feedback, {
        state: { backgroundPath: normalizedPathname },
      });
      return;
    }

    closeRouteModal();
  }, [closeRouteModal, navigate, normalizedPathname]);

  const handleSettingsTabChange = React.useCallback((tabKey) => {
    const nextPath = SETTINGS_TAB_TO_ROUTE[tabKey];
    if (!nextPath || nextPath === normalizedPathname) return;

    navigate(nextPath, {
      replace: true,
      state: location.state,
    });
  }, [location.state, navigate, normalizedPathname]);

  const dismissUpgradePage = React.useCallback(() => {
    clearUpgradePrompt?.();
    const backgroundPath = location.state?.backgroundPath;
    const returnPath = isAppPath(backgroundPath) && backgroundPath !== APP_ROUTES.upgrade
      ? backgroundPath
      : APP_ROUTES.chat;

    navigate(returnPath, { replace: true });
  }, [clearUpgradePrompt, location.state, navigate]);

  // Single orchestrator: profile check always runs before billing check.
  // Runs whenever session or billingStatus changes. Cancels stale runs on cleanup.
  React.useEffect(() => {
    if (!session?.userId || !billingStatus) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await financialService.getProfile();
        if (cancelled) return;

        if (res?.ok && !res?.data) {
          // New user — show intake first, never billing
          setIntakeOpen(true);
          return;
        }
      } catch (_) {
        if (cancelled) return;
      }

      // Profile exists (or check errored) — now check billing
      if (billingStatus.billing_status === 'setup_required') {
        setBillingSetupOpen(!upgradePageOpen);
      } else {
        setBillingSetupOpen(false);
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.userId, billingStatus, upgradePageOpen]);

  if (intakeOpen) {
    return (
      <IntakeWizard
        onCompleted={async () => {
          setIntakeOpen(false);
          loadThreads();
          await refreshBilling();
          navigate(APP_ROUTES.chat, { replace: true });
        }}
      />
    );
  }

  return (
    <div className="app" data-history-open={historyOpen ? 'true' : 'false'}>
      {/* Live region for app toasts/announcements */}
      <div className="visually-hidden" aria-live="polite" aria-atomic="true" id="app-announcer"></div>
      <main className="app__main">
        <aside className="sidebar" id="sidepanel" aria-label="History">
          <SidePanel
            onThreadSelected={() => setHistoryOpen(false)}
            onClose={() => setHistoryOpen(false)}
          />
        </aside>
        <section className="content" aria-label="Conversation">
          <header className="app__header">
            {activeThread && (
              <span className="app__thread-title">{activeThread.title || 'New conversation'}</span>
            )}
            <FeedbackButton
              open={feedbackRouteOpen}
              onOpenChange={handleFeedbackOpenChange}
            />
          </header>
          <div className="content__body">
            {/* Mobile toggle to open history since the TopBar is removed */}
            <button
              className="mobile-history-toggle"
              aria-label="Open chat history"
              aria-controls="sidepanel"
              aria-expanded={historyOpen ? 'true' : 'false'}
              onClick={() => setHistoryOpen(true)}
            >
              ☰
            </button>
            <div className="content__scroll">
              {dashboardRouteOpen ? <DashboardPage /> : <ConversationArea />}
            </div>
          </div>
          {!dashboardRouteOpen && (
            <div className="content__composer">
              <InputBar />
            </div>
          )}
        </section>
      </main>
      {/* Mobile backdrop for history overlay */}
      <div
        className="app-backdrop"
        aria-hidden="true"
        onClick={() => setHistoryOpen(false)}
      />
      {/* Modals */}
      <ProfileModal
        open={settingsRouteOpen}
        onClose={closeRouteModal}
        initialTab={settingsInitialTab}
        onTabChange={handleSettingsTabChange}
        needsPlaidConnect={needsPlaidConnect}
        onDismissPlaidConnect={() => setNeedsPlaidConnect(false)}
        onUpgradeRequired={(message) => navigate(APP_ROUTES.upgrade, {
          state: { upgradeMessage: message, backgroundPath: normalizedPathname },
        })}
      />
      <BillingSetup
        open={billingSetupOpen}
        onComplete={() => {
          setBillingSetupOpen(false);
          clearUpgradePrompt?.();
          refreshBilling();
          loadThreads();
        }}
      />
      <PaymentFailedNotice
        open={billingStatus?.billing_status === 'past_due'}
        onResolved={() => {
          refreshBilling();
        }}
      />
      {upgradePageOpen && (
        <UpgradePage
          message={upgradePageMessage}
          onDismiss={dismissUpgradePage}
          onComplete={() => {
            refreshBilling();
            loadThreads();
          }}
        />
      )}
    </div>
  );
}

export default function AppShell() {
  const navigate = useNavigate();
  const handleThreadCreated = React.useCallback((threadId) => {
    navigate(getChatThreadPath(threadId), {
      replace: true,
      state: { preserveConversation: true },
    });
  }, [navigate]);

  return (
    <ConversationProvider onThreadCreated={handleThreadCreated}>
      <AppShellInner />
    </ConversationProvider>
  );
}
